import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { buildSingleAddItemData } from 'features/AddItemsToOrder/lib/buildAddItemsToOrderRequestData';
import { addItemsToOrderService } from 'features/AddItemsToOrder/model/services/addItemsToOrderService';
import { addItemsToOrderActions } from 'features/AddItemsToOrder/model/slices/addItemsToOrderSlice';
import { editOrderItem } from 'features/editItemsInOrder/model/services/editOrderItem/editOrderItem';
import { deleteOrderItem } from 'features/removeItemsInOrder/model/services/deleteOrderItem/deleteOrderItem';

import type { AppDispatch } from 'app/providers/StoreProvider';
import type {
    MappedCatalogItem,
    NewlyAddedItem,
} from 'entities/CatalogItem/model/types/CatalogItemSchema';

export interface ApplyOrderSelectionQuantityLocalParams {
    dispatch: AppDispatch;
    selectionKey: string;
    itemUid: string;
    nextQty: number;
}

export interface SyncOrderSelectionQuantityParams {
    dispatch: AppDispatch;
    selectedOrderId: string;
    orderLineId: string;
    nextQty: number;
}

export const applyOrderSelectionQuantityLocal = ({
    dispatch,
    selectionKey,
    itemUid,
    nextQty,
}: ApplyOrderSelectionQuantityLocalParams) => {
    if (nextQty < 1) {
        dispatch(catalogItemActions.removeMultiselectItem(selectionKey));
        dispatch(catalogItemActions.removeNewItem(selectionKey));
        if (itemUid !== selectionKey) {
            dispatch(catalogItemActions.removeNewItem(itemUid));
        }
        return;
    }

    dispatch(
        catalogItemActions.setItemQuantity({
            id: selectionKey,
            value: nextQty,
        }),
    );
};

export const syncOrderSelectionQuantity = async ({
    dispatch,
    selectedOrderId,
    orderLineId,
    nextQty,
}: SyncOrderSelectionQuantityParams) => {
    dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(true));

    try {
        if (nextQty < 1) {
            await dispatch(
                deleteOrderItem({
                    orderId: selectedOrderId,
                    itemId: orderLineId,
                }),
            );
            return;
        }

        await dispatch(
            editOrderItem({
                orderId: selectedOrderId,
                itemId: orderLineId,
                qty: nextQty,
            }),
        );
    } finally {
        dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(false));
    }
};

export const applyOrderSelectionQuantity = async ({
    dispatch,
    selectionKey,
    itemUid,
    nextQty,
    selectedOrderId,
    orderLineId,
}: ApplyOrderSelectionQuantityLocalParams & {
    selectedOrderId?: string;
    orderLineId?: string;
}) => {
    applyOrderSelectionQuantityLocal({
        dispatch,
        selectionKey,
        itemUid,
        nextQty,
    });

    if (!selectedOrderId) {
        return;
    }

    await syncOrderSelectionQuantity({
        dispatch,
        selectedOrderId,
        orderLineId: orderLineId ?? selectionKey,
        nextQty,
    });
};

export interface RestoreOrderSelectionParams {
    dispatch: AppDispatch;
    selectionKey: string;
    item: Partial<MappedCatalogItem>;
    newlyAdded?: NewlyAddedItem;
    selectedOrderId?: string;
}

export const restoreOrderSelection = async ({
    dispatch,
    selectionKey,
    item,
    newlyAdded,
    selectedOrderId,
}: RestoreOrderSelectionParams) => {
    const qty = item.qty && item.qty > 0 ? item.qty : 1;
    const uid = item.uid ?? item.uuid;
    const catalogItemId = item.catalogItemId;

    dispatch(
        catalogItemActions.addMultiselectItem({
            id: selectionKey,
            ...(uid !== undefined && { uid }),
            ...(catalogItemId !== undefined && { catalogItemId }),
            ...(item.title !== undefined && { title: item.title }),
            ...(item.brand !== undefined && { brand: item.brand }),
            ...(item.thumbnailUrl !== undefined && {
                thumbnailUrl: item.thumbnailUrl,
            }),
            ...(item.sku !== undefined && { sku: item.sku }),
            ...(item.propertyCategoryId !== undefined && {
                propertyCategoryId: item.propertyCategoryId,
            }),
        }),
    );

    if (qty !== 1) {
        dispatch(
            catalogItemActions.setItemQuantity({
                id: selectionKey,
                value: qty,
            }),
        );
    }

    if (newlyAdded) {
        dispatch(catalogItemActions.addNewItem(newlyAdded));
    }

    if (!selectedOrderId) {
        return;
    }

    const data =
        catalogItemId != null && catalogItemId !== ''
            ? buildSingleAddItemData('catalog', {
                  itemId: catalogItemId,
                  qty,
              })
            : buildSingleAddItemData('marketplace', {
                  itemId: uid ?? selectionKey,
                  qty,
              });

    dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(true));

    try {
        await dispatch(
            addItemsToOrderService({
                orderId: selectedOrderId,
                data,
            }),
        );
    } finally {
        dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(false));
    }
};
